import React, { useEffect, useState } from "react";
import { valueToString, displayPercent } from "helpers/Helpers";
import { useParams, useHistory } from "react-router";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { showError, showSuccess, enqueue, dequeue } from "redux/actions/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";

import {
  GetInvoice,
  GetInvoiceLineItem,
  GetDownloadInvoice,
  PutVoidInvoice,
  PostDuplicateInvoice,
  PutApplyDeposit,
  PutDeposit,
  GetInvoiceTransaction,
  PostInvoiceTransactionRefund,
  PostBalanceAdjustment,
  PostEmailInvoice,
} from "api/rpc/2024-04/facilityAdmin/order/invoice";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import { Badge } from "components/badge/Badge";
import InvoicePaymentModal from "./InvoicePaymentModal";
import { IInvoice, IInvoiceLineItem, IInvoiceTransaction } from "redux/reducers/models/order";
import { loadPaymentOptions } from "redux/actions/facility";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { ButtonNew as Button } from "components/buttonNew";
import DepositCallout from "components/depositCallout/DepositCallout";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";

import "pages/secure/facility/order/order.scss";
import Input from "components/form/input";
import { useTranslation } from "react-i18next";
import EmailInvoiceModal, { IEMail } from "./modals/EmailModal";

export default function Invoice() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const terminalStore = useAppSelector(store => store.terminalStore);
  const { t } = useTranslation();
  const { processRefund } = useStripeTerminalPayment();

  const [invoice, setInvoice] = useState<IInvoice>(undefined);
  const [parentInvoice, setParentInvoice] = useState<IInvoice>(undefined);
  const [invoiceLineItems, setInvoiceLineItems] = useState<Array<IInvoiceLineItem>>(undefined);
  const [paymentModalVisible, setPaymentModalVisible] = useState<boolean>(false);

  const {
    state: voidInvoicePopup,
    closeModal: closeVoidInvoicePopup,
    updateModal: updateVoidInvoicePopup,
  } = useModal();

  const {
    state: duplicateInvoicePopup,
    closeModal: closeDuplicateInvoicePopup,
    updateModal: updateDuplicateInvoicePopup,
  } = useModal();

  const {
    state: addAdjustmentsPopup,
    closeModal: closeAddAdjustmentsPopup,
    updateModal: updateAddAdjustmentsPopup,
  } = useModal();

  const {
    state: applyDepositPopup,
    updateModal: updateApplyDepositPopup,
    closeModal: closeApplyDepositPopup,
  } = useModal({ depositId: null });

  const {
    state: removeDepositPopup,
    updateModal: updateRemoveDepositPopup,
    closeModal: closeRemoveDepositPopup,
  } = useModal({ depositId: null });

  const {
    state: adjustBalancePopup,
    updateModal: updateAdjustBalancePopup,
    closeModal: closeAdjustBalancePopup,
  } = useModal({
    newBalance: 0,
  });

  const {
    state: sendInvoiceSheet,
    updateModal: updateSendInvoiceSheet,
    closeModal: closeSendInvoiceSheet,
  } = useModal<IEMail>({
    newEmail: "",
    emails: [],
    invoiceID: null,
  });

  useEffect(() => {
    void dispatch(loadPaymentOptions());
  }, []);

  useEffect(() => {
    void loadInvoice();
  }, [id]);

  async function loadInvoice() {
    const invoiceRes = await GetInvoice(
      {
        id: Number(id),
        extended: true,
      },
      true,
    );

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.001")));
      return;
    }

    // If invoice has a parent invoice load it
    if (invoiceRes.data[0].parent_id) {
      const parentRes = await GetInvoice({ id: invoiceRes.data[0].parent_id }, true);

      if (invoiceRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.002")));
      } else {
        setParentInvoice(parentRes.data[0]);
      }
    }

    invoiceRes.data[0].transactions = determineRefundedTransactions(invoiceRes.data[0]?.transactions);

    if (invoiceRes.data[0].tournament_id) {
      const tournamentRes = await GetTournament({ id: invoiceRes.data[0].tournament_id }, true);

      if (tournamentRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.003")));
        return;
      }

      invoiceRes.data[0].tournament = tournamentRes.data[0];

      const depositRes = await GetInvoiceTransaction({ tournament_id: tournamentRes.data[0].id }, true);

      if (depositRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.004")));
        return;
      }

      invoiceRes.data[0].tournament.deposits = determineRefundedTransactions(depositRes.data);
    } else if (invoiceRes.data[0].hospitality_event_id) {
      const eventRes = await GetEvent({ id: invoiceRes.data[0].hospitality_event_id }, true);

      if (eventRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.005")));
        return;
      }

      invoiceRes.data[0].hospitality_event = eventRes.data[0];

      const depositRes = await GetInvoiceTransaction({ hospitality_event_id: eventRes.data[0].id }, true);

      if (depositRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.006")));
        return;
      }

      invoiceRes.data[0].hospitality_event.deposits = determineRefundedTransactions(depositRes.data);
    }

    const lineItemRes = await GetInvoiceLineItem({ invoice_id: invoiceRes.data[0].id }, true);

    if (lineItemRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.007")));
      return;
    }

    setInvoice(invoiceRes.data[0]);
    setInvoiceLineItems(lineItemRes.data);
  }

  async function downloadInvoice() {
    const invoiceRes = await GetDownloadInvoice({ invoice_id: Number(id) }, true);

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.008")));
      return;
    }

    window.open(String(invoiceRes.data));
  }

  async function voidInvoice() {
    const voidRes = await PutVoidInvoice({ invoice_id: Number(id) }, true);

    if (voidRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.009")));
      return;
    }

    void loadInvoice();
    updateVoidInvoicePopup({ isOpen: false });
  }

  async function duplicateInvoice() {
    const duplicateRes = await PostDuplicateInvoice({ invoice_id: Number(id) }, true);

    if (duplicateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.010")));
      return;
    }

    updateDuplicateInvoicePopup({ isOpen: false });
    dispatch(showSuccess(t("secure.facility.order.invoices.invoice.011")));
  }

  async function addAdjustments() {
    const duplicateRes = await PostDuplicateInvoice(
      {
        invoice_id: Number(id),
        adjustment: true,
      },
      true,
    );

    if (duplicateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.012")));
      return;
    }

    closeAddAdjustmentsPopup();
    dispatch(showSuccess(t("secure.facility.order.invoices.invoice.013")));
    history.push(`/admin/invoices/${String(duplicateRes.data.id)}/edit`);
  }

  async function applyDeposit(depositId: number) {
    if (!depositId) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.014")));
      return;
    }

    if (invoice.financial_status !== "open") {
      dispatch(showError(t("secure.facility.order.invoices.invoice.015")));
      return;
    }

    const applyRes = await PutApplyDeposit(
      {
        invoice_id: Number(id),
        deposit_id: depositId,
      },
      true,
    );

    if (applyRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.016")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice.017")));
    updateApplyDepositPopup({ isOpen: false, depositId: null });
    void loadInvoice();
  }

  async function removeDeposit(depositId: number) {
    if (!depositId) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.018")));
      return;
    }

    const applyRes = await PutDeposit(
      {
        invoice_id: null,
        deposit_id: depositId,
      },
      true,
    );

    if (applyRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.019")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice.020")));
    updateRemoveDepositPopup({ isOpen: false, depositId: null });
    void loadInvoice();
  }

  function getBalance(): number {
    if (!invoice?.transactions) {
      return;
    }

    let total = invoice?.total_price;

    for (let i = 0; i < invoice?.transactions.length; i++) {
      if (invoice?.transactions[i].kind === "refund" || invoice?.transactions[i].kind === "invoice_debit_adjustment") {
        total += invoice?.transactions[i].amount;
      } else {
        total -= invoice?.transactions[i].amount;
      }
    }

    return total;
  }

  /** Refunded transaction based off parent_id of the transaction == the currentTransactionId */
  function determineRefundedTransactions(transactions: IInvoiceTransaction[]): IInvoiceTransaction[] {
    const filteredTransactions = transactions.filter(
      (transaction: Record<string, any>) => transaction?.kind !== "deposit_authorization",
    );
    const refundedTransactions = transactions.filter(
      (transaction: Record<string, any>) => transaction?.parent_id && transaction?.kind === "refund",
    );

    const markRefunds = [...filteredTransactions]?.reduce(
      (markedRefunds, currentTransaction, index) => {
        const refunded = refundedTransactions?.some(
          (transaction: Record<string, any>) => transaction?.parent_id === currentTransaction?.id,
        );

        // Adding in 'refunded' param to HouseAccountTransactionType
        if (refunded) {
          markedRefunds[index] = { ...markedRefunds[index], refunded: true };
        } else {
          markedRefunds[index] = { ...markedRefunds[index], refunded: false };
        }
        return markedRefunds;
      },
      [...filteredTransactions],
    );

    return markRefunds;
  }

  async function postRefund(transaction: IInvoiceTransaction) {
    //Handle interac refunds
    if (transaction?.payment_type === "interac") {
      if (terminalStore?.reader) {
        dispatch(enqueue());
        const refundRes = await processRefund(transaction?.amount, transaction?.payment_intent_id);
        if (refundRes === "success") {
          dispatch(dequeue());
          dispatch(showSuccess(t("secure.facility.order.invoices.invoice.021")));
        } else {
          dispatch(dequeue());
          dispatch(showSuccess(t("secure.facility.order.invoices.invoice.022")));
          return;
        }
      } else {
        dispatch(showSuccess(t("secure.facility.order.invoices.invoice.023")));
        return;
      }
    }

    const refundRes = await PostInvoiceTransactionRefund({ transaction_id: transaction?.id }, true);

    if (refundRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.024")));
      return;
    }

    void loadInvoice();
  }

  async function sendInvoice() {
    const params = {
      invoice_id: Number(id),
      emails: sendInvoiceSheet.emails,
    };

    const res = await PostEmailInvoice(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error occurred while sending email")); //TODO: Translations
      return;
    }

    dispatch(showSuccess("Invoices successfully sent")); //TODO: Translations
    closeSendInvoiceSheet();
  }

  async function createBalanceAdjustment() {
    const adjustmentRes = await PostBalanceAdjustment(
      {
        invoice_id: invoice?.id,
        new_balance: adjustBalancePopup.newBalance,
      },
      true,
    );

    if (adjustmentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating balance adjustment"))); // TODO: Translation
      return;
    }

    void loadInvoice();
    closeAdjustBalancePopup();
  }

  function navigateToParentInvoice() {
    const url =
      parentInvoice?.financial_status === "draft"
        ? `/admin/invoices/${parentInvoice.id}/edit`
        : `/admin/invoices/${parentInvoice.id}`;

    history.push(url);
  }

  return (
    <Page
      title={`${t("secure.facility.order.invoices.invoice.025")} ${invoice?.name}`}
      titleMetadata={generateStatusBadge(invoice?.financial_status)}
      breadcrumbs={[{ prefix: true, label: t("secure.facility.order.invoices.invoice.026"), url: "/admin/invoices" }]}
      splitLayout
      multipleActionDropdownAction={{
        label: t("secure.facility.order.invoices.invoice.027"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.028"),
              icon: "money-bill",
              handler: () => setPaymentModalVisible(true),
              disabled: invoice?.financial_status === "paid" || invoice?.financial_status === "voided",
            },
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.029"),
              icon: "download",
              handler: () => void downloadInvoice(),
            },
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.030"),
              icon: "ban",
              handler: () => updateVoidInvoicePopup({ isOpen: true }),
              disabled: invoice?.financial_status !== "open",
            },
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.031"),
              icon: "copy",
              handler: () => updateDuplicateInvoicePopup({ isOpen: true }),
              disabled: invoice?.financial_status === "voided",
            },
            {
              type: "handler",
              label: "Send To",
              icon: "mail-forward",
              handler: () => updateSendInvoiceSheet({ isOpen: true, emails: [invoice?.customer?.email] }),
            },
          ],
        },
      }}
    >
      <Page.Section twoThirds>
        <Card title={t("secure.facility.order.invoices.invoice.032")}>
          <Card.Section>
            {invoiceLineItems &&
              invoiceLineItems?.map((item: any, index: number) => {
                return (
                  <Card.SubSection key={index}>
                    <div className="order-line-item">
                      <div>
                        <p className="text-lg text-semibold">{item.product_title}</p>
                        {item.product_title !== item.variant_title ? (
                          <p className="text-sm text-gray-500">{item.variant_title}</p>
                        ) : null}
                      </div>
                      <div className="text-right">
                        <p className="text-lg text-gray-500">
                          {item.quantity} x{" "}
                          {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
                            Number(item.price),
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg text-semibold">
                          {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
                            item.price * item.quantity,
                          )}
                        </p>
                      </div>
                    </div>

                    {item.modifiers ? (
                      <div>
                        {item.modifiers.map((modifier: any, i: number) => {
                          return (
                            <div key={i}>
                              <div className="order-line-item">
                                <div style={{ color: "#475467", marginLeft: "15px" }}>{modifier.product_title}</div>
                                <div className="text-right">
                                  <p className="text-lg text-gray-500">
                                    {modifier.quantity} x{" "}
                                    {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
                                      Number(modifier.price),
                                    )}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg text-semibold">
                                    {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
                                      modifier.price * modifier.quantity,
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </Card.SubSection>
                );
              })}
          </Card.Section>
          <Card.Section>
            <div className="flex justify-end">
              <table>
                <tbody>
                  <tr>
                    <td></td>
                    <td>{t("secure.facility.order.invoices.invoice.033")}</td>
                    <td className="text-right">
                      <div className="pl-4">
                        {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
                          invoice?.subtotal_price,
                        )}
                      </div>
                    </td>
                  </tr>
                  {invoice?.tax_lines?.map((tax_line: Record<string, any>, index: number) => {
                    return (
                      <tr key={index}>
                        <td></td>
                        <td>
                          <span>
                            {tax_line.title}{" "}
                            <span className="text-md text-gray-500">{displayPercent(Number(tax_line.rate))}</span>
                          </span>
                        </td>
                        <td className="text-right">
                          {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
                            Number(tax_line.price),
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td></td>
                    <td>{t("secure.facility.order.invoices.invoice.034")}</td>
                    <td className="text-right">
                      {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(invoice?.total_price)}
                    </td>
                  </tr>
                  {invoice?.transactions?.map((transaction: IInvoiceTransaction, index: number) => (
                    <tr key={index}>
                      {invoice?.financial_status !== "voided" &&
                      transaction.kind !== "refund" &&
                      !transaction.refunded ? (
                        <td>
                          <NavigationDropdownNew
                            showPlainTextLabel
                            leftAlign
                            label={<FontAwesomeIcon className="pr-2" icon={faChevronDown} />}
                            sections={[
                              [
                                {
                                  label: t("secure.facility.order.invoices.invoice.035"),
                                  icon: "turn-down-left",
                                  onClick: () => void postRefund(transaction),
                                },
                              ],
                            ]}
                          />
                        </td>
                      ) : (
                        <td></td>
                      )}
                      <td>
                        <span>
                          {transaction.kind === "deposit"
                            ? t("secure.facility.order.invoices.invoice.036")
                            : transaction.kind === "refund"
                            ? t("secure.facility.order.invoices.invoice.037")
                            : t("secure.facility.order.invoices.invoice.038")}
                          {valueToString(transaction.payment_type)}
                        </span>
                        {transaction.payment_last_4 ? (
                          <span className="text-gray-500">•••• {transaction.payment_last_4}</span>
                        ) : null}
                      </td>
                      <td className="text-right">
                        {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
                          transaction.kind === "refund" || transaction.kind === "invoice_debit_adjustment"
                            ? transaction.amount * -1
                            : transaction.amount,
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td></td>
                    <td className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice.039")}</td>
                    <td className="text-right text-semibold text-lg">
                      {Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(getBalance())}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card.Section>
          {invoice?.financial_status === "open" && (
            <Card.Section>
              <Button type="primary" onClick={() => updateAddAdjustmentsPopup({ isOpen: true })}>
                {t("secure.facility.order.invoices.invoice.040")}
              </Button>
            </Card.Section>
          )}
          {invoice?.financial_status === "paid" && invoice?.balance < 0 && (
            <Card.Section>
              <Button type="primary" onClick={() => updateAdjustBalancePopup({ isOpen: true })}>
                {t("Adjust Balance")}
              </Button>
            </Card.Section>
          )}
        </Card>
      </Page.Section>
      <Page.Section oneThird>
        <Card title={t("secure.facility.order.invoices.invoice.041")}>
          <Card.Section>
            <div>
              {invoice?.customer ? (
                <div>
                  <p className="text-md">
                    {t("secure.facility.order.invoices.invoice.042")} {invoice?.date}
                  </p>
                  <p className="text-md">
                    {t("secure.facility.order.invoices.invoice.043")} {invoice?.due_date}
                  </p>
                </div>
              ) : (
                <div>
                  <p>{t("secure.facility.order.invoices.invoice.044")}</p>
                </div>
              )}
            </div>
          </Card.Section>
        </Card>
        {invoice?.parent_id && (
          <Card
            title={t("secure.facility.order.invoices.invoice.045")}
            titleActions={[
              {
                content: t("secure.facility.order.invoices.invoice.046"),
                action: navigateToParentInvoice,
              },
            ]}
          >
            <Card.Section>
              <div>
                <p className="text-md text-semibold">{parentInvoice?.name}</p>
              </div>
            </Card.Section>
          </Card>
        )}
        <Card title={t("secure.facility.order.invoices.invoice.047")}>
          <Card.Section>
            <div>
              {invoice?.customer ? (
                <div className="flex-col">
                  <div>
                    <p className="text-md text-semibold">{invoice?.customer?.full_name}</p>
                    <p className="text-md">{invoice?.customer?.email}</p>
                    <p className="text-md">{invoice?.customer?.phone}</p>
                  </div>
                  {invoice?.billing_address ? (
                    <div className="mt-2">
                      <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice.048")}</p>
                      <p className="text-md">{invoice?.billing_address?.address_line_1}</p>
                      {invoice?.billing_address?.address_line_2 && (
                        <p className="text-md" key="address-line-2">
                          {invoice.billing_address.address_line_2}
                        </p>
                      )}
                      <p className="text-md">{`${invoice?.billing_address?.city}, ${invoice?.billing_address?.province_code}`}</p>
                      <p className="text-md">{invoice?.billing_address?.postal}</p>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice.048")}</p>
                      <p className="text-md">{t("secure.facility.order.invoices.invoice.049")}</p>
                    </div>
                  )}
                  {invoice?.shipping_address ? (
                    <div className="mt-2">
                      <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice.050")}</p>
                      <p className="text-md">{invoice?.shipping_address?.address_line_1}</p>
                      {invoice?.shipping_address?.address_line_2 && (
                        <p className="text-md" key="address-line-2">
                          {invoice.shipping_address.address_line_2}
                        </p>
                      )}
                      <p className="text-md">{`${invoice?.shipping_address?.city}, ${invoice?.shipping_address?.province_code}`}</p>
                      <p className="text-md">{invoice?.shipping_address?.postal}</p>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice.050")}</p>
                      <p className="text-md">{t("secure.facility.order.invoices.invoice.049")}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p>{t("secure.facility.order.invoices.invoice.051")}</p>
                </div>
              )}
            </div>
          </Card.Section>
        </Card>
        {invoice?.tournament && (
          <Card title={t("secure.facility.order.invoices.invoice.052")}>
            <Card.Section>
              <div>
                {invoice?.tournament ? (
                  <div>
                    <p className="text-md text-semibold">{invoice?.tournament?.name}</p>
                    <p className="text-md">{`${invoice?.tournament?.total_participant_count}/${Number(
                      invoice?.tournament?.player_limit,
                    )} ${t("secure.facility.order.invoices.invoice.053")}`}</p>{" "}
                    {}
                    <p className="text-md">{invoice?.tournament?.date}</p>
                    {invoice?.tournament?.deposits && invoice?.financial_status === "open" ? (
                      <div className="flex flex-col gap-2 mt-2">
                        {invoice?.tournament?.deposits
                          ?.filter(deposit => deposit.invoice_id === invoice?.id || deposit.invoice_id === null)
                          ?.filter(deposit => !deposit.refunded && deposit.kind !== "refund")
                          ?.map((deposit, index) => {
                            return (
                              <DepositCallout
                                key={deposit?.id}
                                type={"info"}
                                title={`${t("secure.facility.order.invoices.invoice.054")} ${index + 1}`}
                                content={new Intl.NumberFormat("en-CA", {
                                  style: "currency",
                                  currency: invoice?.currency,
                                }).format(deposit?.amount)}
                                actions={{
                                  primary: {
                                    label: deposit?.invoice_id
                                      ? t("secure.facility.order.invoices.invoice.055")
                                      : t("secure.facility.order.invoices.invoice.056"),
                                    onClick: () =>
                                      deposit?.invoice_id
                                        ? updateRemoveDepositPopup({ isOpen: true, depositId: deposit?.id })
                                        : updateApplyDepositPopup({ isOpen: true, depositId: deposit?.id }),
                                  },
                                }}
                                applied={deposit?.invoice_id !== null}
                              />
                            );
                          })}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div>
                    <p>{t("secure.facility.order.invoices.invoice.057")}</p>
                  </div>
                )}
              </div>
            </Card.Section>
          </Card>
        )}
        {invoice?.hospitality_event && (
          <Card title={t("secure.facility.order.invoices.invoice.058")}>
            <Card.Section>
              <div>
                {invoice?.hospitality_event ? (
                  <div>
                    <p className="text-lg">{invoice?.hospitality_event?.name}</p>
                    <p className="text-lg">{invoice?.hospitality_event?.date}</p>
                    {invoice?.hospitality_event?.deposits && invoice?.financial_status === "open" ? (
                      <div className="flex flex-col gap-2 mt-2">
                        {invoice?.hospitality_event?.deposits
                          ?.filter(deposit => deposit.invoice_id === invoice?.id || deposit.invoice_id === null)
                          ?.filter(deposit => !deposit.refunded && deposit.kind !== "refund")
                          ?.map((deposit, index) => {
                            return (
                              <DepositCallout
                                key={deposit?.id}
                                type={"info"}
                                title={`${t("secure.facility.order.invoices.invoice.059")} ${index + 1}`}
                                content={new Intl.NumberFormat("en-CA", {
                                  style: "currency",
                                  currency: invoice?.currency,
                                }).format(deposit?.amount)}
                                actions={{
                                  primary: {
                                    label: deposit?.invoice_id
                                      ? t("secure.facility.order.invoices.invoice.060")
                                      : t("secure.facility.order.invoices.invoice.061"),
                                    onClick: () =>
                                      deposit?.invoice_id
                                        ? updateRemoveDepositPopup({ isOpen: true, depositId: deposit?.id })
                                        : updateApplyDepositPopup({ isOpen: true, depositId: deposit?.id }),
                                  },
                                }}
                                applied={deposit?.invoice_id !== null}
                              />
                            );
                          })}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div>
                    <p>{t("secure.facility.order.invoices.invoice.062")}</p>
                  </div>
                )}
              </div>
            </Card.Section>
          </Card>
        )}
      </Page.Section>

      {invoice && paymentModalVisible && (
        <InvoicePaymentModal
          paymentModalVisible={paymentModalVisible}
          invoice={invoice}
          paymentMethods={null} // TODO: Load current customer payment methods
          onOk={() => void loadInvoice()}
          closePaymentModal={() => setPaymentModalVisible(false)}
        />
      )}

      {invoice && sendInvoiceSheet && (
        <EmailInvoiceModal
          state={sendInvoiceSheet}
          customerEmail={invoice?.customer?.email}
          update={updateSendInvoiceSheet}
          close={closeSendInvoiceSheet}
          onOk={() => void sendInvoice()}
        />
      )}

      <Portal isMounted={voidInvoicePopup.isOpen}>
        <Popup
          open={voidInvoicePopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.063")}
          description={t("secure.facility.order.invoices.invoice.064")}
          onOk={voidInvoice}
          okText={t("secure.facility.order.invoices.invoice.065")}
          onCancel={closeVoidInvoicePopup}
          type="warning"
        />
      </Portal>

      <Portal isMounted={duplicateInvoicePopup.isOpen}>
        <Popup
          open={duplicateInvoicePopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.066")}
          description={t("secure.facility.order.invoices.invoice.067")}
          onOk={duplicateInvoice}
          okText={t("secure.facility.order.invoices.invoice.068")}
          onCancel={closeDuplicateInvoicePopup}
          type="warning"
        />
      </Portal>

      <Portal isMounted={addAdjustmentsPopup.isOpen}>
        <Popup
          open={addAdjustmentsPopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.069")}
          description={t("secure.facility.order.invoices.invoice.070")}
          onOk={addAdjustments}
          okText={t("secure.facility.order.invoices.invoice.071")}
          onCancel={closeAddAdjustmentsPopup}
          type="warning"
        />
      </Portal>

      <Portal isMounted={applyDepositPopup.isOpen}>
        <Popup
          open={applyDepositPopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.072")}
          description={t("secure.facility.order.invoices.invoice.073")}
          type="warning"
          onCancel={() => closeApplyDepositPopup()}
          onOk={() => applyDeposit(Number(applyDepositPopup.depositId))}
        />
      </Portal>

      <Portal isMounted={removeDepositPopup.isOpen}>
        <Popup
          open={removeDepositPopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.074")}
          description={t("secure.facility.order.invoices.invoice.075")}
          type="warning"
          onCancel={() => closeRemoveDepositPopup()}
          onOk={() => removeDeposit(Number(removeDepositPopup.depositId))}
        />
      </Portal>

      <Portal isMounted={adjustBalancePopup.isOpen}>
        <Popup
          open={adjustBalancePopup.isOpen}
          title={t("Adjust Balance?")}
          description={t("Are you sure you want to adjust the balance of this this invoice?")} // TODO: Translation
          type="warning"
          onCancel={() => closeAdjustBalancePopup()}
          onOk={() => createBalanceAdjustment()}
        >
          <div className="mt-2">
            <Input
              label={t("New Balance")}
              value={adjustBalancePopup.newBalance}
              onChange={e => updateAdjustBalancePopup({ newBalance: Number(e.target.value) })}
              type={"number"}
            />
          </div>
        </Popup>
      </Portal>
    </Page>
  );
}

function generateStatusBadge(status: string) {
  switch (status) {
    case "draft":
      return <Badge type="gray">{valueToString(status, "capitalize")}</Badge>;
    case "open":
      return <Badge type="primary">{valueToString(status, "capitalize")}</Badge>;
    case "paid":
      return <Badge type="success">{valueToString(status, "capitalize")}</Badge>;
    case "partially_paid":
      return <Badge type="warning">{valueToString(status, "capitalize")}</Badge>;
    case "voided":
      return <Badge type="error">{valueToString(status, "capitalize")}</Badge>;
    default:
      return <Badge type="gray">{valueToString(status, "capitalize")}</Badge>;
  }
}
